import { jsonRequest, request, socialUrl } from './http.js';
import type { SocialResult } from './http.js';
import { toAcceptedInvite, toInvite, toList, toSentInvite, toVoid, toWrapped } from './decode.js';
import type { AcceptedInvite, Invite, OnlineGame, SentInvite } from './types.js';

export interface SendInviteInput {
  toUserId: string;
  /** WS namespace slug — the Worker validates it against the same four games. */
  game: OnlineGame;
  /** The code of a room the sender has already created. */
  roomCode: string;
}

/**
 * Errors worth surfacing distinctly: `forbidden` = the recipient's
 * `invites_from` setting blocked it, `rate_limited` = the per-recipient
 * cooldown (its `retryAfterSeconds` says how long).
 */
export function sendInvite(input: SendInviteInput): Promise<SocialResult<SentInvite>> {
  return jsonRequest(socialUrl('/invites'), 'POST', input, (body) =>
    toWrapped(body, 'invite', toSentInvite)
  );
}

/** The caller's pending, unexpired, *received* invites. */
export function listInvites(): Promise<SocialResult<Invite[]>> {
  return request(socialUrl('/invites'), { method: 'GET' }, (body) =>
    toList(body, 'invites', toInvite)
  );
}

/**
 * On success the caller joins the returned room via the existing `#room=CODE`
 * path. `conflict` = someone already answered it, `expired` = past its TTL.
 */
export function acceptInvite(id: string): Promise<SocialResult<AcceptedInvite>> {
  return jsonRequest(socialUrl('/invites/accept'), 'POST', { id }, toAcceptedInvite);
}

export function declineInvite(id: string): Promise<SocialResult<void>> {
  return jsonRequest(socialUrl('/invites/decline'), 'POST', { id }, toVoid);
}
